"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { toast } from "sonner";
import { LawFirmsAPI } from "../../../api/lawFirms";
import {
  AdminListLawFirmsParams,
  CreateLawFirmInviteRequest,
  CreateLawFirmLawyerRequest,
  CreateLawFirmRequest,
  LawFirm,
  LawFirmInviteDTO,
  LawyerDTO,
  UpdateLawFirmRequest
} from "../../../api/types";

type FetchLawFirmsOptions = {
  append?: boolean;
};

type LawFirmsContextValue = {
  lawFirms: LawFirm[];
  selectedLawFirm: LawFirm | null;
  isLoadingList: boolean;
  isLoadingDetail: boolean;
  isSaving: boolean;
  error: Error | null;
  nextPageToken: string | null;
  listParams: AdminListLawFirmsParams;
  fetchLawFirms: (
    params?: AdminListLawFirmsParams,
    options?: FetchLawFirmsOptions
  ) => Promise<void>;
  fetchLawFirm: (lawFirmId: string) => Promise<void>;
  createLawFirm: (data: CreateLawFirmRequest) => Promise<LawFirm>;
  updateLawFirm: (
    lawFirmId: string,
    data: UpdateLawFirmRequest
  ) => Promise<LawFirm>;
  deleteLawFirm: (lawFirmId: string) => Promise<void>;
  createLawyer: (
    lawFirmId: string,
    data: CreateLawFirmLawyerRequest
  ) => Promise<LawyerDTO>;
  createInvite: (
    lawFirmId: string,
    data: CreateLawFirmInviteRequest
  ) => Promise<LawFirmInviteDTO>;
  clearSelectedLawFirm: () => void;
};

const LawFirmsContext = createContext<LawFirmsContextValue | null>(null);

const showToast = (
  variant: "success" | "error" | "neutral" | "brand",
  title: string,
  description: string
) => {
  const notify =
    variant === "error"
      ? toast.error
      : variant === "brand"
      ? toast.info
      : toast.success;
  notify(title, { description, duration: 5000 });
};

const mergeLawFirmPreservingSubscriptionDetails = (
  previous: LawFirm | null,
  next: LawFirm
): LawFirm => {
  if (!previous || previous.uid !== next.uid) return next;

  const shouldPreserveSubscription =
    !next.subscription &&
    !next.latest_subscription &&
    !next.subscription_status;
  const shouldPreservePlan =
    !next.subscription_plan &&
    !next.latest_subscription_plan &&
    !next.subscription_plan_type;

  return {
    ...previous,
    ...next,
    subscription: shouldPreserveSubscription
      ? previous.subscription ?? previous.latest_subscription ?? null
      : next.subscription,
    latest_subscription: shouldPreserveSubscription
      ? previous.latest_subscription ?? previous.subscription ?? null
      : next.latest_subscription,
    subscription_status: shouldPreserveSubscription
      ? previous.subscription_status ??
        previous.subscription?.subscription_status ??
        previous.latest_subscription?.subscription_status ??
        null
      : next.subscription_status,
    subscription_plan: shouldPreservePlan
      ? previous.subscription_plan ?? previous.latest_subscription_plan ?? null
      : next.subscription_plan,
    latest_subscription_plan: shouldPreservePlan
      ? previous.latest_subscription_plan ?? previous.subscription_plan ?? null
      : next.latest_subscription_plan,
    subscription_plan_type: shouldPreservePlan
      ? previous.subscription_plan_type ??
        previous.subscription_plan?.subscription_plan_type ??
        previous.latest_subscription_plan?.subscription_plan_type ??
        null
      : next.subscription_plan_type
  };
};

export const useLawFirms = () => {
  const context = useContext(LawFirmsContext);
  if (!context) {
    throw new Error("useLawFirms must be used within a LawFirmsProvider");
  }
  return context;
};

export const LawFirmsProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [lawFirms, setLawFirms] = useState<LawFirm[]>([]);
  const [selectedLawFirm, setSelectedLawFirm] = useState<LawFirm | null>(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [listParams, setListParams] = useState<AdminListLawFirmsParams>({});
  const listParamsRef = useRef<AdminListLawFirmsParams>({});
  const detailRequestSeqRef = useRef(0);

  useEffect(() => {
    listParamsRef.current = listParams;
  }, [listParams]);

  const fetchLawFirms = useCallback(
    async (
      params?: AdminListLawFirmsParams,
      options?: FetchLawFirmsOptions
    ) => {
      setIsLoadingList(true);
      setError(null);
      const mergedParams = { ...listParamsRef.current, ...params };
      try {
        const response = await LawFirmsAPI.adminList(mergedParams);
        setLawFirms((prev) =>
          options?.append ? [...prev, ...response.lawFirms] : response.lawFirms
        );
        setNextPageToken(response.nextPageToken ?? null);
        listParamsRef.current = mergedParams;
        setListParams(mergedParams);
      } catch (err) {
        setError(err as Error);
        showToast("error", "Error fetching law firms", (err as Error).message);
      } finally {
        setIsLoadingList(false);
      }
    },
    []
  );

  const fetchLawFirm = useCallback(async (lawFirmId: string) => {
    detailRequestSeqRef.current += 1;
    const requestSeq = detailRequestSeqRef.current;
    setIsLoadingDetail(true);
    setError(null);
    setSelectedLawFirm((prev) => (prev?.uid === lawFirmId ? prev : null));
    try {
      const lawFirm = await LawFirmsAPI.getById(lawFirmId);
      if (requestSeq !== detailRequestSeqRef.current) return;
      setSelectedLawFirm(lawFirm);
    } catch (err) {
      if (requestSeq !== detailRequestSeqRef.current) return;
      setError(err as Error);
      showToast(
        "error",
        "Error fetching law firm details",
        (err as Error).message
      );
    } finally {
      if (requestSeq === detailRequestSeqRef.current) {
        setIsLoadingDetail(false);
      }
    }
  }, []);

  const createLawFirm = useCallback(async (data: CreateLawFirmRequest) => {
    setIsSaving(true);
    setError(null);
    try {
      const created = await LawFirmsAPI.create(data);
      setSelectedLawFirm(created);
      setLawFirms((prev) => [created, ...prev]);
      showToast(
        "success",
        "Law firm created",
        "The new law firm has been added successfully."
      );
      return created;
    } catch (err) {
      setError(err as Error);
      showToast("error", "Error creating law firm", (err as Error).message);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const updateLawFirm = useCallback(
    async (lawFirmId: string, data: UpdateLawFirmRequest) => {
      setIsSaving(true);
      setError(null);
      try {
        const updated = await LawFirmsAPI.update(lawFirmId, data);
        let mergedUpdated = updated;
        setSelectedLawFirm((prev) => {
          mergedUpdated = mergeLawFirmPreservingSubscriptionDetails(
            prev,
            updated
          );
          return mergedUpdated;
        });
        setLawFirms((prev) =>
          prev.map((firm) =>
            firm.uid === updated.uid
              ? mergeLawFirmPreservingSubscriptionDetails(firm, mergedUpdated)
              : firm
          )
        );
        showToast(
          "success",
          "Law firm updated",
          "The law firm details have been saved successfully."
        );
        return mergedUpdated;
      } catch (err) {
        setError(err as Error);
        showToast("error", "Error updating law firm", (err as Error).message);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  const deleteLawFirm = useCallback(async (lawFirmId: string) => {
    setIsSaving(true);
    setError(null);
    try {
      await LawFirmsAPI.delete(lawFirmId);
      setLawFirms((prev) => prev.filter((firm) => firm.uid !== lawFirmId));
      setSelectedLawFirm((prev: LawFirm | null) =>
        prev?.uid === lawFirmId ? null : prev
      );
      showToast(
        "success",
        "Law firm deleted",
        "The law firm has been removed successfully."
      );
    } catch (err) {
      setError(err as Error);
      showToast("error", "Error deleting law firm", (err as Error).message);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const createLawyer = useCallback(
    async (lawFirmId: string, data: CreateLawFirmLawyerRequest) => {
      setIsSaving(true);
      setError(null);
      try {
        const lawyer = await LawFirmsAPI.createLawyer(lawFirmId, data);
        showToast(
          "success",
          "Lawyer created",
          "The user has been added to the law firm successfully."
        );
        return lawyer;
      } catch (err) {
        setError(err as Error);
        showToast("error", "Error creating lawyer", (err as Error).message);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  const createInvite = useCallback(
    async (lawFirmId: string, data: CreateLawFirmInviteRequest) => {
      setIsSaving(true);
      setError(null);
      try {
        const invite = await LawFirmsAPI.createInvite(lawFirmId, data);
        showToast(
          "success",
          "Invite created",
          "The invitation has been created successfully."
        );
        return invite;
      } catch (err) {
        setError(err as Error);
        showToast("error", "Error creating invite", (err as Error).message);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  const clearSelectedLawFirm = useCallback(() => {
    setSelectedLawFirm(null);
  }, []);

  const value = useMemo(
    () => ({
      lawFirms,
      selectedLawFirm,
      isLoadingList,
      isLoadingDetail,
      isSaving,
      error,
      nextPageToken,
      listParams,
      fetchLawFirms,
      fetchLawFirm,
      createLawFirm,
      updateLawFirm,
      deleteLawFirm,
      createLawyer,
      createInvite,
      clearSelectedLawFirm
    }),
    [
      lawFirms,
      selectedLawFirm,
      isLoadingList,
      isLoadingDetail,
      isSaving,
      error,
      nextPageToken,
      listParams,
      fetchLawFirms,
      fetchLawFirm,
      createLawFirm,
      updateLawFirm,
      deleteLawFirm,
      createLawyer,
      createInvite,
      clearSelectedLawFirm
    ]
  );

  return (
    <LawFirmsContext.Provider value={value}>
      {children}
    </LawFirmsContext.Provider>
  );
};
